// Export registration hook. Pages call useRegisterExport(config, deps) to
// publish their session info + supported download formats; the global Export
// button in the top nav renders whatever the current page registered. When
// no page has registered, the button is disabled so the nav layout stays
// stable. State is stored in the reporting Redux slice rather than React
// context because the top nav and the route element are sibling subtrees
// that share no wrapping provider.
import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@frontend/react'
import {
  selectReportingExportConfig,
  setReportingExportConfig,
  type ExportConfig
} from '@frontend/core/lib/domains/reporting'

export type { ExportConfig }

export function useExportConfig(): ExportConfig | null {
  return useAppSelector(selectReportingExportConfig)
}

// Registers an export config for the current page. Clears on unmount unless
// another page has already taken ownership (prevents clobbering during a
// navigation transition).
export function useRegisterExport(
  config: ExportConfig | null,
  deps: React.DependencyList
) {
  const dispatch = useAppDispatch()
  const stableConfig = useMemo(
    () => config,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps
  )

  useEffect(() => {
    if (!stableConfig) return
    dispatch(setReportingExportConfig(stableConfig))
    return () => {
      dispatch(setReportingExportConfig(null))
    }
  }, [stableConfig, dispatch])
}
